import { api } from "@/lib/api";

export async function getSettings() {
  const r = await api.get("/delivery/settings");
  return r.data;
}

export async function saveSettings(payload) {
  const r = await api.put("/delivery/settings", payload);
  return r.data;
}

export async function testConnection(payload = {}) {
  const r = await api.post("/delivery/test-connection", payload);
  return r.data;
}

export async function bootstrap(days = 90) {
  const r = await api.post(`/delivery/bootstrap?days=${days}`);
  return r.data;
}

export async function triggerSync(days = 30, only_active = true) {
  const r = await api.post("/delivery/sync", { days, only_active });
  return r.data;
}

export async function syncOne(awb) {
  const r = await api.post(`/delivery/sync/${encodeURIComponent(awb)}`);
  return r.data;
}

export async function listShipments({ search = "", status = "all", county = "", days = 90, sort = "newest", limit = 50, skip = 0 } = {}) {
  const params = new URLSearchParams();
  if (search) params.set("search", search);
  if (status) params.set("status", status);
  if (county) params.set("county", county);
  if (days) params.set("days", String(days));
  if (sort) params.set("sort", sort);
  params.set("limit", String(limit));
  params.set("skip", String(skip));
  const r = await api.get(`/delivery/shipments?${params.toString()}`);
  return r.data;
}

export async function getShipment(awb) {
  const r = await api.get(`/delivery/shipments/${encodeURIComponent(awb)}`);
  return r.data;
}

export async function getDashboard(days = 30) {
  const r = await api.get(`/delivery/dashboard?days=${days}`);
  return r.data;
}

export async function getCounties(days = 30) {
  const r = await api.get(`/delivery/dashboard/counties?days=${days}`);
  return r.data;
}

// ---------- Status display helpers ----------
export const STATUS_LABELS = {
  pending: "În așteptare preluare",
  in_transit: "În tranzit",
  out_for_delivery: "În livrare",
  delivered: "Livrat",
  delivery_failed: "Livrare eșuată",
  refused: "Refuzat",
  returned: "Retur expeditor",
  lost: "Pierdut",
  damaged: "Avariat",
  unknown: "Necunoscut",
};

export const STATUS_COLORS = {
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  in_transit: "bg-sky-50 text-sky-700 border-sky-200",
  out_for_delivery: "bg-indigo-50 text-indigo-700 border-indigo-200",
  delivered: "bg-emerald-50 text-emerald-700 border-emerald-200",
  delivery_failed: "bg-orange-50 text-orange-700 border-orange-200",
  refused: "bg-rose-50 text-rose-700 border-rose-200",
  returned: "bg-rose-50 text-rose-700 border-rose-200",
  lost: "bg-zinc-100 text-zinc-700 border-zinc-300",
  damaged: "bg-orange-50 text-orange-700 border-orange-200",
  unknown: "bg-zinc-50 text-zinc-500 border-zinc-200",
};

export const STATUS_DOT = {
  pending: "bg-amber-500",
  in_transit: "bg-sky-500",
  out_for_delivery: "bg-indigo-500",
  delivered: "bg-emerald-500",
  delivery_failed: "bg-orange-500",
  refused: "bg-rose-500",
  returned: "bg-rose-600",
  lost: "bg-zinc-500",
  damaged: "bg-orange-600",
  unknown: "bg-zinc-300",
};

// ---------- COD confirmation queue ----------
export async function listConfirmationQueue(params = {}) {
  const qs = new URLSearchParams();
  Object.entries({
    status: "pending",
    level: "all",
    search: "",
    sort: "risk_desc",
    limit: 100,
    skip: 0,
    ...params,
  }).forEach(([k, v]) => qs.set(k, String(v)));
  const r = await api.get(`/delivery/confirmation-queue?${qs.toString()}`);
  return r.data;
}

export async function getQueueStats() {
  const r = await api.get("/delivery/confirmation-queue/stats");
  return r.data;
}

export async function confirmOrder(shopifyId, note) {
  const r = await api.post(
    `/delivery/confirmation-queue/${encodeURIComponent(shopifyId)}/confirm`,
    { note: note || null },
  );
  return r.data;
}

export async function markNoAnswer(shopifyId, note) {
  const r = await api.post(
    `/delivery/confirmation-queue/${encodeURIComponent(shopifyId)}/no-answer`,
    { note: note || null },
  );
  return r.data;
}

export async function cancelQueueOrder(shopifyId, note) {
  const r = await api.post(
    `/delivery/confirmation-queue/${encodeURIComponent(shopifyId)}/cancel`,
    { note: note || null },
  );
  return r.data;
}

export async function addQueueNote(shopifyId, text) {
  const r = await api.post(
    `/delivery/confirmation-queue/${encodeURIComponent(shopifyId)}/notes`,
    { text },
  );
  return r.data;
}

export async function runQueueBackfill(days) {
  const r = await api.post(
    `/delivery/confirmation-queue/backfill${days ? `?days=${days}` : ""}`,
  );
  return r.data;
}

export async function rescoreQueue() {
  const r = await api.post("/delivery/confirmation-queue/rescore-all");
  return r.data;
}

export async function getRiskSettings() {
  const r = await api.get("/delivery/risk-settings");
  return r.data;
}

export async function saveRiskSettings(patch) {
  const r = await api.put("/delivery/risk-settings", patch);
  return r.data;
}

// ---------- Shipment actions (notes + call flag) ----------
export async function getShipmentNotes(awb) {
  const r = await api.get(`/delivery/shipments/${encodeURIComponent(awb)}/notes`);
  return r.data;
}

export async function addShipmentNote(awb, text) {
  const r = await api.post(
    `/delivery/shipments/${encodeURIComponent(awb)}/notes`,
    { text },
  );
  return r.data;
}

export async function toggleCallFlag(awb) {
  const r = await api.post(
    `/delivery/shipments/${encodeURIComponent(awb)}/toggle-call-flag`,
  );
  return r.data;
}

export async function getRepeatRefusers(days = 90, limit = 20) {
  const r = await api.get(
    `/delivery/insights/repeat-refusers?days=${days}&limit=${limit}`,
  );
  return r.data;
}

// ---------- Risk badge helpers ----------
export async function getAnalyticsRefusalByCod(days = 90) {
  const r = await api.get(`/delivery/analytics/refusal-by-cod?days=${days}`);
  return r.data;
}
export async function getAnalyticsRefusalByTransit(days = 90) {
  const r = await api.get(`/delivery/analytics/refusal-by-transit?days=${days}`);
  return r.data;
}
export async function getAnalyticsNewVsRecurring(days = 90) {
  const r = await api.get(`/delivery/analytics/new-vs-recurring?days=${days}`);
  return r.data;
}
export async function getAnalyticsWeeklyTrend(weeks = 26) {
  const r = await api.get(`/delivery/analytics/weekly-trend?weeks=${weeks}`);
  return r.data;
}
export async function addTrendAnnotation(at, label) {
  const r = await api.post("/delivery/analytics/annotations", { at, label });
  return r.data;
}
export async function deleteTrendAnnotation(id) {
  const r = await api.delete(`/delivery/analytics/annotations/${id}`);
  return r.data;
}

// ---------- Today (Partea 5) ----------
export async function getTodayPanel() {
  const r = await api.get("/delivery/today");
  return r.data;
}

// ---------- Alerts (Partea 2) ----------
export async function getAlertsConfig() {
  const r = await api.get("/delivery/alerts/config");
  return r.data;
}
export async function saveAlertsConfig(patch) {
  const r = await api.put("/delivery/alerts/config", patch);
  return r.data;
}
export async function listAlerts(unread_only = false, limit = 50) {
  const r = await api.get(`/delivery/alerts?unread_only=${unread_only}&limit=${limit}`);
  return r.data;
}
export async function getAlertsUnread() {
  const r = await api.get("/delivery/alerts/unread-count");
  return r.data;
}
export async function markAlertsRead(ids = null) {
  const r = await api.post("/delivery/alerts/mark-read", { ids });
  return r.data;
}
export async function resolveAlert(id) {
  const r = await api.post(`/delivery/alerts/${encodeURIComponent(id)}/resolve`);
  return r.data;
}
export const RISK_COLORS = {
  low: "bg-emerald-50 text-emerald-700 border-emerald-200",
  medium: "bg-amber-50 text-amber-800 border-amber-300",
  high: "bg-rose-50 text-rose-700 border-rose-300",
};
export const RISK_LABEL = { low: "Scăzut", medium: "Mediu", high: "Ridicat" };
